import { useCallback, useEffect, useRef } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { GiftAddressRegion } from '@shared/api/giftOrder/giftOrder.interface';
import { isSameRegion, pickRegion } from '@shared/utils/giftAddressRegion';

import {
  GiftDeliveryAddressState,
  giftDeliveryAddressAtom,
} from '../state/giftDeliveryAddress.atom';
import {
  consumeGiftInputApplyRequest,
  giftInputApplyRequestAtom,
} from '../state/giftInputApply.atom';
import { isOwnedByBugo } from '../state/giftInputOwner';

interface UseGiftDeliveryAddressMemoryParams {
  /** 현재 기본주소 입력값 */
  address: string;
  /** 현재 우편번호 입력값 */
  postalCode: string;
  /** 현재 상세주소 입력값 */
  addressDetail: string;
  /** 카카오 응답 원문(시도·읍면동 등) — 권역 배송비 판정용. 주소와 함께 저장/복원 */
  region: GiftAddressRegion | undefined;
  setAddress: (value: string) => void;
  setPostalCode: (value: string) => void;
  setAddressDetail: (value: string) => void;
  setRegion: (value: GiftAddressRegion | undefined) => void;
  /** 배송형 && 일괄배송(Bulk)일 때만 저장/복원/칩 활성화 */
  enabled: boolean;
  /** 현재 주문서의 부고 — 이 부고로 저장된 주소만 복원한다 (명의 섞임 방지) */
  bugoId: string | null;
}

interface UseGiftDeliveryAddressMemoryResult {
  /** localStorage에 보존된 최근 배송지 (없으면 null) */
  saved: GiftDeliveryAddressState | null;
  /** 저장된 배송지를 입력 폼에 적용 */
  applySaved: () => void;
  /** 칩(재적용 버튼) 노출 여부 — 저장값이 있고 현재 입력값과 다를 때 */
  showChip: boolean;
  /** 입력값과 저장값(localStorage)을 모두 비운다 */
  clear: () => void;
  /** 초기화 버튼 노출 여부 — 입력값 또는 저장값이 하나라도 있을 때 */
  canClear: boolean;
}

/**
 * 일괄배송 최근 배송지를 localStorage(recoil-persist)에 보존/복원하는 공통 훅.
 * 바로구매/장바구니결제 두 페이지에서 동일하게 사용한다.
 *
 * - 저장: 유효한 배송지(주소+우편번호)가 입력될 때마다 atom에 기록 → 결제 실패/새로고침에도 보존.
 * - 자동 복원: 마운트 시 입력값이 비어있고 **같은 부고로 저장된** 값이 있으면 silent로 채움.
 * - 재적용: 사용자가 주소를 지우거나 바꾼 경우 칩을 통해 저장값을 다시 적용.
 * - 담아오기: 대리주문 패널의 "주문서에 채우기" 요청(giftInputApplyRequestAtom)도 여기서 받는다.
 */
export const useGiftDeliveryAddressMemory = ({
  address,
  postalCode,
  addressDetail,
  region,
  setAddress,
  setPostalCode,
  setAddressDetail,
  setRegion,
  enabled,
  bugoId,
}: UseGiftDeliveryAddressMemoryParams): UseGiftDeliveryAddressMemoryResult => {
  const [stored, setSaved] = useRecoilState(giftDeliveryAddressAtom);
  const prefilledRef = useRef(false);

  //* 다른 상주(부고)로 저장된 주소는 없는 것으로 본다 — 명의 섞임 방지
  const saved = stored && isOwnedByBugo(stored.ownerBugoId, bugoId) ? stored : null;

  // 저장값을 입력 폼에 적용
  const applySaved = useCallback(() => {
    if (!saved) return;
    setAddress(saved.address);
    setPostalCode(saved.postalCode);
    setAddressDetail(saved.addressDetail ?? '');
    setRegion(pickRegion(saved.region)); // 구버전 저장분은 undefined → 권역 브랜드면 재검색 유도
  }, [saved, setAddress, setPostalCode, setAddressDetail, setRegion]);

  // 마운트 시 1회 자동 복원 (입력값이 비어있을 때만)
  useEffect(() => {
    if (!enabled || prefilledRef.current) return;
    if (!saved) return;
    if (address.trim()) return; // 이미 입력된 값 보호
    prefilledRef.current = true;
    applySaved();
  }, [enabled, saved, address, applySaved]);

  // 대리주문 패널에서 "이 주소로 채우기"를 누른 경우 — 직원이 보고 누른 값이므로
  // 입력값이 있어도 덮어쓴다. (요청마다 requestedAt이 달라 같은 값도 다시 적용됨)
  const applyRequest = useRecoilValue(giftInputApplyRequestAtom);
  useEffect(() => {
    const requested = applyRequest?.deliveryAddress;
    if (!enabled || !requested || !bugoId) return;
    if (applyRequest?.bugoId !== bugoId) return;
    if (!consumeGiftInputApplyRequest('address-apply', applyRequest.requestedAt)) return;
    prefilledRef.current = true;
    setAddress(requested.address);
    setPostalCode(requested.postalCode);
    setAddressDetail(requested.addressDetail ?? '');
    setRegion(pickRegion(requested.region));
  }, [
    enabled,
    bugoId,
    applyRequest,
    setAddress,
    setPostalCode,
    setAddressDetail,
    setRegion,
  ]);

  // 대리주문 패널의 "주문서 정리" — 배송지는 입력값·보존값을 모두 비운다
  //* 배송지 입력이 실제로 있는 화면(enabled)만 요청을 소비한다 — 쿠폰형 주문서가
  //* 먼저 소비해버리면 정작 배송형 주문서에선 정리가 안 된다
  useEffect(() => {
    if (!enabled || !applyRequest?.resetToMemberDefault || !bugoId) return;
    if (applyRequest.bugoId !== bugoId) return;
    if (!consumeGiftInputApplyRequest('address-reset', applyRequest.requestedAt)) return;
    prefilledRef.current = true; // 초기화 직후 자동 복원으로 되살아나는 것 방지
    setAddress('');
    setPostalCode('');
    setAddressDetail('');
    setRegion(undefined);
    setSaved(null);
  }, [
    enabled,
    applyRequest,
    bugoId,
    setAddress,
    setPostalCode,
    setAddressDetail,
    setRegion,
    setSaved,
  ]);

  // 유효한 배송지 입력 시 저장 (지금 보고 있는 부고의 것으로 도장)
  useEffect(() => {
    if (!enabled || !bugoId) return;
    if (!address.trim() || !postalCode.trim()) return;
    const next: GiftDeliveryAddressState = {
      address,
      postalCode,
      addressDetail: addressDetail.trim() ? addressDetail : undefined,
      region: pickRegion(region),
      ownerBugoId: bugoId,
    };
    setSaved((prev) =>
      prev &&
      prev.address === next.address &&
      prev.postalCode === next.postalCode &&
      prev.addressDetail === next.addressDetail &&
      isSameRegion(prev.region, next.region) &&
      prev.ownerBugoId === next.ownerBugoId
        ? prev
        : next,
    );
  }, [enabled, bugoId, address, postalCode, addressDetail, region, setSaved]);

  // 입력값 + 저장값(localStorage) 모두 초기화
  const clear = useCallback(() => {
    setAddress('');
    setPostalCode('');
    setAddressDetail('');
    setRegion(undefined);
    setSaved(null);
    prefilledRef.current = true; // 초기화 직후 자동 복원으로 되살아나는 것 방지
  }, [setAddress, setPostalCode, setAddressDetail, setRegion, setSaved]);

  const showChip = enabled && !!saved && saved.address !== address;
  const canClear = enabled && (!!saved || !!address.trim() || !!addressDetail.trim());

  return { saved, applySaved, showChip, clear, canClear };
};
